import { ConnectionStatus, MinigridArchitecture, MinigridLiveSnapshot } from "../../../../types/minigridtypes";
import { WeatherInfo } from "../../../../../../services/weatherAPI";
import FlowDiagram from "./FlowDiagram";
import LiveDashboardCombinedChart from "./LiveDashboardCombinedChart";
import ArchitecturePanel from "./ArchitecturePanel";
import FilterV2 from "../../../../../filter/components/FilterV2";
import { FilterPoint } from "../../../../../filter/types/types";
import { MomiTelemetryFilter } from "../../../../service/momiTelemetryAPI";
import styles from "./LiveDashboardView.module.scss";

import { LiveEnergySegment } from "../../../../liveEnergyDashboardConfig";

export interface LiveDashboardViewProps {
    snapshot: MinigridLiveSnapshot | null;
    weather: WeatherInfo | null;
    weatherError: string | null;
    liveError: string | null;
    isInitialLoading: boolean;
    onEnergyNodeNavigate?: (segment: LiveEnergySegment) => void;
    momiUuid: string;
    filter: FilterPoint;
    architecture: MinigridArchitecture | null;
    architectureLoading: boolean;
    architectureError: string | null;
}

const STATUS_LABEL: Record<ConnectionStatus, string> = {
    online: "MoMi en ligne",
    stale: "Données obsolètes",
    offline: "MoMi hors ligne",
};

/** WMO weather code → emoji icon */
function weatherIcon(code: number | undefined): string {
    if (code === undefined) return "🌡️";
    if (code === 0) return "☀️";
    if (code <= 2) return "⛅";
    if (code === 3) return "☁️";
    if (code <= 48) return "🌫️";
    if (code <= 55) return "🌦️";
    if (code <= 65) return "🌧️";
    if (code <= 75) return "🌨️";
    if (code <= 82) return "🌧️";
    if (code <= 99) return "⛈️";
    return "🌡️";
}

export default function LiveDashboardView({
    snapshot,
    weather,
    weatherError,
    liveError,
    isInitialLoading,
    onEnergyNodeNavigate,
    momiUuid,
    filter,
    architecture,
    architectureLoading,
    architectureError,
}: LiveDashboardViewProps) {
    const telemetryFilter: MomiTelemetryFilter | null =
        filter.startDate && filter.stopDate
            ? {
                  startDate: filter.startDate,
                  stopDate: filter.stopDate,
                  aggregateWindow: filter.aggregateWindow || "1h",
              }
            : null;
    const status: ConnectionStatus = snapshot?.connection_status ?? "offline";
    const lastUpdate = snapshot?.updated_at
        ? new Date(snapshot.updated_at).toLocaleTimeString()
        : "--";

    return (
        <div className={styles.dashboard}>
            <div className={styles.header}>
                <h2 className={styles.title}>Dashboard en direct</h2>
                <div
                    className={`${styles.status} ${styles[`status_${status}`]}`}
                    title={`Dernière mise à jour: ${lastUpdate}`}
                >
                    <span className={styles.statusDot} />
                    {STATUS_LABEL[status]}
                </div>
            </div>

            {liveError && (
                <div className={styles.error}>{liveError}</div>
            )}

            {isInitialLoading && !liveError && (
                <div className={styles.loading}>Chargement des données en direct…</div>
            )}

            {!isInitialLoading && !liveError && (
                <>
                    <FlowDiagram
                        snapshot={snapshot}
                        onNodeNavigate={onEnergyNodeNavigate}
                    />

                    <ArchitecturePanel
                        architecture={architecture}
                        isLoading={architectureLoading}
                        error={architectureError}
                    />

                    {momiUuid && (
                        <div className={styles.chartsSection}>
                            <div className={styles.chartsHeader}>
                                Synthèse — consommation, solaire, groupe, SoC batterie
                            </div>
                            <div className={styles.filterBar}>
                                <FilterV2 />
                            </div>
                            {telemetryFilter && (
                                <div className={styles.chartWrapper}>
                                    <LiveDashboardCombinedChart
                                        momiUuid={momiUuid}
                                        filter={telemetryFilter}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            <div className={styles.weather}>
                <div className={styles.weatherLabel}>Météo locale</div>
                {weatherError && (
                    <div className={styles.weatherError}>{weatherError}</div>
                )}
                {!weatherError && weather && (
                    <div className={styles.weatherContent}>
                        <span className={styles.weatherIcon}>
                            {weatherIcon(weather.weather_code)}
                        </span>
                        <span className={styles.weatherTemp}>
                            {weather.temperature_c.toFixed(1)}°C
                        </span>
                        <span className={styles.weatherDesc}>
                            {weather.description} · Vent {weather.wind_speed_kmh.toFixed(0)} km/h
                        </span>
                    </div>
                )}
                {!weatherError && !weather && (
                    <div className={styles.weatherContent}>--</div>
                )}
            </div>
        </div>
    );
}

